import { api } from '../client'
import type { BinaryResponse, RequestOptions } from '../client'
import { toScore } from '../../composables/coerce'
import type {
  Attachment,
  BulkDeleteResult,
  ProcurementDetail,
  ProcurementFilters,
  ProcurementSummary,
} from './types'

function buildQuery(f: ProcurementFilters): string {
  const params = new URLSearchParams()
  if (f.search) params.set('search', f.search)
  if (f.sourceId !== undefined) params.set('sourceId', String(f.sourceId))
  if (f.status) params.set('status', f.status)
  if (f.procurementType) params.set('procurementType', f.procurementType)
  if (f.country) params.set('country', f.country)
  const minAiScore = toScore(f.minAiScore)
  if (minAiScore !== undefined) params.set('minAiScore', String(minAiScore))
  if (f.cpv) params.set('cpv', f.cpv)
  if (f.createdSince) params.set('createdSince', f.createdSince)
  if (f.notificationSince) params.set('notificationSince', f.notificationSince)
  const minValueEur = toScore(f.minValueEur)
  if (minValueEur !== undefined) params.set('minValueEur', String(minValueEur))
  const maxValueEur = toScore(f.maxValueEur)
  if (maxValueEur !== undefined) params.set('maxValueEur', String(maxValueEur))
  if (f.currency) params.set('currency', f.currency)
  if (f.sort) params.set('sort', f.sort)
  if (f.order) params.set('order', f.order)
  if (f.limit !== undefined) params.set('limit', String(f.limit))
  if (f.offset !== undefined) params.set('offset', String(f.offset))
  if (f.archived !== undefined) params.set('archived', String(f.archived))
  if (f.filterId !== undefined) params.set('filterId', String(f.filterId))
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

export const procurementsHttpApi = {
  count: (filters: ProcurementFilters = {}, options?: RequestOptions): Promise<number> =>
    api
      .get<{ count: number }>(`/procurements/count${buildQuery(filters)}`, options)
      .then((r) => r.count),

  sourceTotals: (
    filters: ProcurementFilters = {},
    options?: RequestOptions,
  ): Promise<{ sourceName: string; total: number }[]> =>
    api.get(`/procurements/source-totals${buildQuery(filters)}`, options),

  list: (
    filters: ProcurementFilters = {},
    options?: RequestOptions,
  ): Promise<ProcurementSummary[]> =>
    api.get<ProcurementSummary[]>(`/procurements${buildQuery(filters)}`, options),

  get: (id: number, options?: RequestOptions): Promise<ProcurementDetail> =>
    api.get<ProcurementDetail>(`/procurements/${id}`, options),

  delete: (id: number, options?: RequestOptions): Promise<void> =>
    api.del(`/procurements/${id}`, options),

  bulkDelete: (procurementIds: number[], options?: RequestOptions): Promise<BulkDeleteResult> =>
    api.post<BulkDeleteResult>('/procurements/bulk-delete', { procurementIds }, options),

  downloadAttachment: (attachment: Attachment, options?: RequestOptions): Promise<BinaryResponse> =>
    api.blob(attachment.downloadUrl, options),
}
